/**
 * The geometry the guide needs to lead somebody along a line. No DOM, no map,
 * no clock — every function here is a function of its arguments, which is what
 * lets `nav.test.ts` walk a whole route past the state machine as an array.
 *
 * **Distances along the route are never straight-line distances.** By air it is
 * 90 m from the path to Монплезир and 400 m around the pond, and the number the
 * visitor reads is the one they will walk. That is the whole reason `cumulative`
 * and `distanceAlongTo` exist instead of a `distanceM` call at the point of use.
 *
 * The projection is EQUIRECTANGULAR around the route's own first vertex: at the
 * scale of one park the error is under a tenth of a percent, and it makes a
 * projection onto a segment ordinary planar algebra instead of spherical
 * trigonometry. `distanceM` stays a haversine, because it is also asked about
 * things that are not on the route.
 */

import type { LngLat } from '../map/objects';

export type { LngLat };

const EARTH_M = 6_371_008.8;
const DEG = Math.PI / 180;
/** One degree of latitude, in metres. Constant enough at any latitude. */
const M_PER_DEG_LAT = 111_320;

/** Metres between two points, on the sphere. */
export function distanceM(a: LngLat, b: LngLat): number {
  const dLat = (b[1] - a[1]) * DEG;
  const dLng = (b[0] - a[0]) * DEG;
  const lat1 = a[1] * DEG;
  const lat2 = b[1] * DEG;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Metres east and north of `origin`. */
export function toLocalMeters(p: LngLat, origin: LngLat): [number, number] {
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(origin[1] * DEG);
  return [(p[0] - origin[0]) * mPerDegLng, (p[1] - origin[1]) * M_PER_DEG_LAT];
}

/** The inverse, so a projection computed in metres comes back as a coordinate. */
export function fromLocalMeters(xy: [number, number], origin: LngLat): LngLat {
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(origin[1] * DEG);
  return [origin[0] + xy[0] / mPerDegLng, origin[1] + xy[1] / M_PER_DEG_LAT];
}

export type Snap = {
  /** The segment `[index, index + 1]` the point projects onto. */
  index: number;
  /** How far along that segment, 0…1. */
  t: number;
  /** The projection itself. */
  at: LngLat;
  /** Metres from the point to the line. */
  offRouteM: number;
  /** Metres along the line from its first vertex to the projection. */
  alongM: number;
};

/**
 * Accumulated length at each vertex: `[0, …, total]`.
 *
 * Computed once per route and carried in the route model — it is O(n) and the
 * state machine asks for a distance on every GPS fix.
 */
export function cumulative(line: readonly LngLat[]): number[] {
  const out: number[] = [0];
  if (line.length === 0) return out;
  const origin = line[0]!;
  let running = 0;
  for (let i = 1; i < line.length; i += 1) {
    const a = toLocalMeters(line[i - 1]!, origin);
    const b = toLocalMeters(line[i]!, origin);
    running += Math.hypot(b[0] - a[0], b[1] - a[1]);
    out.push(running);
  }
  return out;
}

/** Projects a point onto a polyline. An empty or one-vertex line has none. */
export function snapToPolyline(line: readonly LngLat[], p: LngLat): Snap | null {
  if (line.length < 2) return null;
  const origin = line[0]!;
  const point = toLocalMeters(p, origin);

  let best: Snap | null = null;
  let running = 0;
  for (let i = 0; i < line.length - 1; i += 1) {
    const a = toLocalMeters(line[i]!, origin);
    const b = toLocalMeters(line[i + 1]!, origin);
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const segment = Math.hypot(dx, dy);
    // A zero-length segment (a duplicated vertex) projects onto its own start.
    const t = segment === 0 ? 0 : clamp01(((point[0] - a[0]) * dx + (point[1] - a[1]) * dy) / (segment * segment));
    const x = a[0] + dx * t;
    const y = a[1] + dy * t;
    const off = Math.hypot(point[0] - x, point[1] - y);
    if (!best || off < best.offRouteM) {
      best = {
        index: i,
        t,
        at: fromLocalMeters([x, y], origin),
        offRouteM: off,
        alongM: running + segment * t,
      };
    }
    running += segment;
  }
  return best;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * Metres ALONG the line between a projected position and a vertex of it.
 *
 * Absolute, so it is also the honest answer for a vertex already behind — the
 * caller decides whether a stop behind them is worth printing.
 */
export function distanceAlongTo(
  line: readonly LngLat[],
  cum: readonly number[],
  from: Snap,
  vertex: number,
): number {
  void line;
  const target = cum[Math.min(Math.max(vertex, 0), cum.length - 1)] ?? 0;
  return Math.abs(target - from.alongM);
}

/**
 * Which vertex of the drawn line each stop stands at.
 *
 * This is the FRONT-END's guess at the split the backend can simply state
 * (`legs[].first`/`last`, design §6.2), and it is used only when those are
 * absent. The search is MONOTONIC — each stop is looked for from the previous
 * stop's vertex onwards — because a walk through a park routinely passes a
 * fountain twice, and a global nearest-vertex search would then anchor stop 3
 * to a vertex before stop 2 and run the whole leg backwards.
 */
export function anchorStops(line: readonly LngLat[], stops: readonly LngLat[]): number[] {
  const out: number[] = [];
  if (line.length === 0) return stops.map(() => 0);
  let from = 0;
  for (const stop of stops) {
    let bestIndex = from;
    let bestDistance = Infinity;
    for (let i = from; i < line.length; i += 1) {
      const d = distanceM(line[i]!, stop);
      if (d < bestDistance) {
        bestDistance = d;
        bestIndex = i;
      }
    }
    out.push(bestIndex);
    from = bestIndex;
  }
  return out;
}

/**
 * Cuts the drawn line at the visitor's position into what is behind and what is
 * ahead.
 *
 * Both halves SHARE the cut point, so the two polylines meet exactly and the
 * walk reads as one line in two colours rather than as two lines with a gap.
 * A half shorter than two vertices is returned empty — MapGL draws nothing for
 * a one-point polyline anyway, and `syncRoute` destroys the line instead.
 */
export function splitRoute(
  line: readonly LngLat[],
  snap: Snap | null,
): { walked: LngLat[]; ahead: LngLat[] } {
  if (line.length < 2) return { walked: [], ahead: [...line] };
  if (!snap) return { walked: [], ahead: [...line] };
  const walked = [...line.slice(0, snap.index + 1), snap.at];
  const ahead = [snap.at, ...line.slice(snap.index + 1)];
  return {
    walked: walked.length >= 2 ? walked : [],
    ahead: ahead.length >= 2 ? ahead : [],
  };
}
